import React, { useMemo } from 'react';

import { Link, useLocation } from 'react-router-dom';
import { type MenuTile } from 'types';

import { sanitizeUrl } from '@braintree/sanitize-url';
import { getThemeClassNameByIndex } from 'utils';

import { AppsList } from 'components/AppsList/AppsList';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import { DEFAULT_TIMEZONE } from 'consts';
import style from './Tiles.module.scss';

interface Props {
  menu: MenuTile;
  index: number;
  styles: Record<string, string | undefined>;
  isLoading?: boolean;
  timezone?: string | null;
}

export const Tile: React.FC<Props> = ({ menu, index, styles, isLoading, timezone }) => {
  const { mmId, name, apps, neverPublished, busyUntil, surcharge } = menu;
  const location = useLocation();
  const sanitisedPathname = sanitizeUrl(location.pathname);
  const className = getThemeClassNameByIndex(index);
  const showSurcharge = surcharge && surcharge.amount > 0;

  const url = useMemo(() => {
    const segments = sanitisedPathname.split('/').filter(Boolean);

    if (segments.length >= 2) {
      return `/${segments[0]}/${segments[1]}/menus/${mmId}`;
    }

    return '';
  }, [sanitisedPathname, mmId]);

  const chip = neverPublished ? <p className={style.Chip}>unpublished menu</p> : null;

  const busyUntilInLocationTimezone = busyUntil
    ? new Date(busyUntil).toLocaleTimeString('en-AU', {
        timeZone: timezone || DEFAULT_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        second: undefined // Exclude seconds
      })
    : null;

  const showBusyUntilChip = busyUntil ? new Date(busyUntil) > new Date() : false;
  const busyUntilChip = showBusyUntilChip ? (
    <p
      data-testid="busy-until-chip"
      className={`${style.Chip} ${style.UnavailableChip}`}
      style={{ left: showSurcharge ? '46px' : undefined }}
    >
      unavailable until {busyUntilInLocationTimezone}
    </p>
  ) : null;

  const surchargeIcon = showSurcharge ? (
    <div className={styles.Surcharge}>
      <span data-testid="tile-surcharge-icon">+{surcharge.amount}%</span>
    </div>
  ) : null;

  const content = (
    <div>
      <h3 data-testid="tile-heading" className={isLoading ? 'LoadingCoverText' : ''}>
        {name}
      </h3>
      {chip}
      {busyUntilChip}
      {surchargeIcon}
      <AppsList isLoading={isLoading} apps={apps} />
      <img
        src={`/temp/graphic-${(index + 1) % 3}.svg`}
        alt=""
        className={`${styles.Graphic} ${isLoading ? styles.Loading : ''}`}
      />
    </div>
  );

  const onMenuTileClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    const appToLog = apps[0].id.includes('virtual') && apps[0].brand ? `${apps[0].name} - brand` : apps[0].name;
    logAmplitudeEvent({
      name: 'click menu card',
      category: AmplitudeEventCategories.MM_ALL_ITEMS,
      properties: {
        [AmplitudeEventProperties.MENU_NAME]: menu.name,
        [AmplitudeEventProperties.APP]: appToLog
      }
    });
    if (isLoading) {
      e.preventDefault();
    }
  };

  return (
    <li
      key={mmId}
      style={{
        transitionDelay: `${index * 0.1}s`
      }}
      data-testid="tile"
    >
      {isLoading ? (
        <div className={`Theme${className} LoadingCoverImage`} data-testid="tile-link-loading">
          {content}
        </div>
      ) : (
        <Link
          to={url}
          className={`Theme${className} ${isLoading ? 'LoadingCoverImage' : ''}`}
          onClick={onMenuTileClick}
          data-testid="tile-link"
        >
          {content}
        </Link>
      )}
    </li>
  );
};
